// src/components/templates/useSamplePages.js
//
// A template's sample manuscript, cut into pages by the editor's own layout.
//
// `reflowPages` (src/utils/paginate.js) is how the editor decides where a page
// ends, and it measures the live DOM — so the sample is poured into a sheet
// that is rendered off screen (not hidden: hidden things measure as zero),
// inside `.editor-main-content` so it picks up exactly the page styles the
// editor and the Book Preview use, at the book's trim size. What comes back is
// one `.rv-page` per sheet, the same shape `splitPages` reads, so the gallery's
// thumbnails and the Book Preview it opens show the same pages.
//
// The caller renders `measure` once, anywhere; it is empty between layouts.
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { createEmptyPageEl, reflowPages } from '../../utils/paginate';
import { sampleBodyHtml } from '../../utils/templateSample';
import { DEFAULT_BOOK_SIZE, pageSizePx } from '../../config/bookSizes';
import TEMPLATE_SAMPLES from './templateSamples';

export default function useSamplePages(template, trimSize = DEFAULT_BOOK_SIZE) {
  const ref = useRef(null);
  const [pages, setPages] = useState(null);
  const { width, height } = useMemo(() => pageSizePx(trimSize), [trimSize]);

  const body = useMemo(
    () => (template ? sampleBodyHtml(template.sections, TEMPLATE_SAMPLES[template.id]) : ''),
    [template],
  );

  useEffect(() => {
    let cancelled = false;
    setPages(null);
    if (!body) return undefined;

    const layOut = async () => {
      // Line breaks depend on the font, so measure once it is in.
      try { await document.fonts?.ready; } catch { /* measure with what there is */ }
      const root = ref.current;
      if (cancelled || !root) return;

      let out;
      try {
        const page = createEmptyPageEl(document);
        page.querySelector('.rv-page-body').innerHTML = body;
        root.replaceChildren(page);
        reflowPages(root);
        out = Array.from(root.querySelectorAll('.rv-page')).map((el) => el.outerHTML);
      } catch {
        // A layout that fails leaves no pages rather than "laying out" forever.
        out = [];
      }
      root.replaceChildren();
      if (!cancelled) setPages(out);
    };
    layOut();
    return () => { cancelled = true; };
  }, [body, width, height]);

  const measure = (
    <div
      ref={ref}
      aria-hidden="true"
      className="editor-main-content"
      style={{
        position: 'fixed',
        left: -100000,
        top: 0,
        pointerEvents: 'none',
        '--rv-page-width': `${width}px`,
        '--rv-page-height': `${height}px`,
      }}
    />
  );

  return { pages, measure, width, height };
}
